import { useState, useCallback, useEffect, useMemo } from "react";

import styles from "./PromptSelection.module.scss";
import { useErrorHandlerContext } from '../../../../contexts/error-handler/ErrorContext';
import { usePrompt } from '../../contexts/PromptContext';
import { useAdaptive } from '../../../../contexts/adaptive/AdaptiveContext';
import { Prompt } from '../../models/PromptTypes';
import { invokePromptChangeEvent } from '../../../chat/hooks/useMessagesState';
import { getClassNames } from '../../../../helpers/classHelpers';
import { ErrorHandler } from '../../../../contexts/error-handler/ErrorHandler';
import Loader from '../../../../ui/loader/Loader';
import { TestIds } from '../../../../mocks/ids';
import PromptViewModal from '../../modals/prompt-view-modal/PromptViewModal';
import Alert from '../../../../ui/alert/Alert';
import labels from '../../../../data/labels';
import { useRouting } from '../../../dashboards/admin/hooks/useRouting';
import useInfo from '../../../../hooks/useInfo';
import MultiSelect from '../../../../ui/select/MultiSelect';
import { categoryOptions } from '../../config';
import { CustomSelectOptionBaseType } from '@danfoss/mosaic-core';
import { filterPrompts, handleSelectChange } from '../../helpers/PromptHelper';
import Input from '../../../../ui/input/Input';
import { searchBy, sortItemsBy } from '../../../../helpers/listsHelpers';
import Button, { ButtonIconPosition, ButtonThemes } from '../../../../ui/button/Button';
import { Icon } from '../../../../ui/icon/Icon';
import { IconTheme } from '../../../../ui/icon/Icon.types';
import { Icons } from '../../../../ui/icon/icons/material';
import PromptModal from '../../modals/prompt-modal/PromptModal';
import { usePromptSaveAction } from '../../hooks/usePromptActions';
import { usePromptModalMode } from '../../hooks/usePromptModalMode';
import PromptCard from '../../../../ui/prompt-card/PromptCard';
import { HideInEmbeddedMode } from '../../../../components/HideInEmbeddedMode';

export const PromptSelection = () => {
    const { errorId } = useErrorHandlerContext();
    const { getPromptsByKey } = usePrompt();
    const { chatInfo } = useAdaptive();

    const { allowedPersonaOptions } = useInfo();
    const [search, setSearch] = useState<string>('');

    const [initialPrompt, setInitialPrompt] = useState<Prompt | undefined>(undefined);
    const [modalMode, setModalMode] = usePromptModalMode();
    const [isModalOpen, setIsModalOpen] = useState(false);

    const [isLoading, setIsLoading] = useState(false);
    const [selectedPrompt, setSelectedPrompt] = useState<Prompt | null>(null);
    const [prompts, setPrompts] = useState<Prompt[]>([]);
    const { getRouteParams } = useRouting();
    const params = getRouteParams('prompt');
    const personaKey = chatInfo.persona || params.assistantId;
    const [selectedCategories, setSelectedCategories] = useState<string[]>([]);

    const fetchPrompts = useCallback(async () => {
        if (!personaKey) return;
        setIsLoading(true);
        try {
            const result = await getPromptsByKey(personaKey);
            const sortedPrompts = sortItemsBy(result, 'updated', true);
            setPrompts(sortedPrompts);
        } catch (err) {
        } finally {
            setIsLoading(false);
        }
    }, [getPromptsByKey, personaKey]);

    const handleClose = useCallback(() => setIsModalOpen(false), []);

    const handleAfterSubmit = useCallback(() => {
        fetchPrompts();
    }, [fetchPrompts]);

    useEffect(() => {
        fetchPrompts();
    }, [chatInfo.persona, fetchPrompts]);

    const searchedPrompts = useMemo(
        () => searchBy(prompts, search, ['title', 'content']),
        [prompts, search]
    );

    const filteredPrompts = useMemo(
        () => filterPrompts(searchedPrompts, { categories: selectedCategories }),
        [searchedPrompts, selectedCategories]
    );

    const onClickCard = useCallback(
        (prompt: Prompt) => {
            setSelectedPrompt(prompt);
        },
        []
    );

    const handleCategoryChange = useCallback(
        (option?: string | string[] | CustomSelectOptionBaseType | CustomSelectOptionBaseType[]) => {
            handleSelectChange(option, setSelectedCategories);
        }, []
    );

    const handleTryPrompt = useCallback((prompt: Prompt) => {
        invokePromptChangeEvent(prompt.content, prompt.key);
        setSelectedPrompt(null);
    }, []);

    const renderSearch = <div className={styles.search}>
        <Input placeholder={labels.promptGallerySearchPlaceholder} value={search} onChange={setSearch} />
    </div>;

    const handleResetFilter = useCallback(() => {
        setSearch('');
        setSelectedCategories([]);
    }, []);

    const handleAddPrompt = usePromptSaveAction(
        {
            id: '',
            created: '',
            type: 'ai',
            content: '',
        },
        personaKey,
        setInitialPrompt,
        setModalMode,
        setIsModalOpen
    );

    const renderAddButton = <Button
        iconPosition={ButtonIconPosition.beginning}
        label={labels.promptListAdd}
        onClick={handleAddPrompt}
        theme={ButtonThemes.secondary}
        icon={
            <Icon.Base
                size={22}
                title=""
                isClickable
                theme={IconTheme.inherit}
                iconName={Icons.add}
            />
        }
    />;

    const renderCard = useCallback(
        (prompt: Prompt) => (
            <div key={prompt.key} className={styles.cardWrapper}>
                <PromptCard
                    inverseHoverEffect={true}
                    className={getClassNames([styles.card])}
                    maxDescriptionLength={200}
                    title={prompt.title}
                    description={prompt.content}
                    chipList={(prompt.tags ?? '')
                        .split(',')
                        .map(tag => tag.trim())
                        .filter(Boolean)}
                    onClick={() => onClickCard(prompt)}
                />
            </div>
        ),
        [onClickCard]
    );
    return (
        <div data-testid={TestIds.documentView}>
            <PromptModal isOpen={isModalOpen} onAfterSubmit={handleAfterSubmit} onClose={handleClose} mode={modalMode} initialValue={initialPrompt} allowTryPrompt={false} />

            <ErrorHandler.Notifications className={styles.error} id={errorId} />

            {isLoading ? (
                <Loader />
            ) : (
                <>
                    <div className={styles.container}>
                        {prompts.length > 0 ? (
                            <>
                                <div className={styles.controlsRow}>
                                    {renderSearch}
                                    <div className={styles.col}>
                                        <MultiSelect
                                            placeholder={labels.filterTask}
                                            value={selectedCategories}
                                            isSearchable
                                            options={categoryOptions}
                                            onChange={handleCategoryChange}
                                        />
                                        <Button
                                            iconPosition={ButtonIconPosition.beginning}
                                            theme={ButtonThemes.textSecondary}
                                            onClick={handleResetFilter}
                                            icon={
                                                <Icon.Base
                                                    size={22}
                                                    title=""
                                                    isClickable
                                                    theme={IconTheme.inherit}
                                                    iconName={Icons.filterReset}
                                                />
                                            }
                                        />
                                    </div>
                                    <HideInEmbeddedMode>
                                        <div>
                                            {renderAddButton}
                                        </div>
                                    </HideInEmbeddedMode>
                                </div>
                                {filteredPrompts.length === 0 && (
                                    <Alert className={styles.notification} description={labels.promptGalleryNoResultsFound} />
                                )}
                                <div className={styles.cardGrid}>
                                    {filteredPrompts.map((prompt) => renderCard(prompt))}
                                </div>
                            </>
                        ) : (
                            <HideInEmbeddedMode>
                                <div className={styles['single-add-button']}>
                                    {renderAddButton}
                                </div>
                            </HideInEmbeddedMode>
                        )}
                    </div>
                </>
            )}

            {(!isLoading && prompts.length === 0) && (
                <Alert className={styles.notification} description={labels.promptGalleryNoResultsFound} />
            )}

            <PromptViewModal
                isOpen={!!selectedPrompt}
                onClose={() => setSelectedPrompt(null)}
                assistantTitle={
                    selectedPrompt
                        ? (allowedPersonaOptions.find(p => p.key === selectedPrompt.assistant_key)?.title ?? '')
                        : ''
                }
                prompt={selectedPrompt}
                onTry={handleTryPrompt}
            />
        </div>
    );
};
